import React, { useState, useEffect, useCallback } from 'react';
import {
  Title, Checkbox, Stack, Group, Button, ScrollArea,
  Accordion, Box, Loader, Text, Badge, Progress, Radio, Alert, Modal,
  Card, Divider, Tabs, Switch, Select, Tooltip, Code
} from '@mantine/core';
import {
  IconDownload, IconCheck, IconX, IconLoader, IconInfoCircle,
  IconAlertTriangle, IconSettings, IconChartBar, IconEye,
  IconTopologyStar, IconBrain, IconSparkles, IconFileText,
  IconRobot
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import Layout from './Layout';
import { useAwsCredentials } from '../../../shared/contexts/CredentialsContext';
import { listConnectInstances } from '../aws/connectClient';
import { fetchResources } from '../aws/resourceDispatcher';
import { createInvocationScanConfig } from '../aws/lambdaClient';
import { ExcelExporter } from '../excel/excelExporter';
import { runtimeDeprecationService } from '../../../shared/services/runtimeDeprecationService';
import { reactFlowContactFlowService } from '../diagrams/reactFlowContactFlowService';
import ContactFlowViewer from '../components/ContactFlowViewer';
import AWSCredentialChangeSection from './AWSCredentialChangeSection';
import { RoutingPresets } from '../diagrams/routing';
import { aiAnalysisService } from '../../../shared/services/aiAnalysisService';
import { wordDocumentService } from '../../../shared/services/wordDocumentService';
import { BEDROCK_MODELS, getRecommendedModel } from '../../../shared/services/bedrockModels';
import { summarizeContactFlow } from '../services/summarizationService';
import { useAuth } from '../../../shared/contexts/AuthContext';
import pLimit from 'p-limit';

// How many contact-flow summaries to request in parallel. The backend Lambda/API Gateway scale
// freely; the practical ceiling is Bedrock's per-model throttle (nova-2-lite). Raise this after
// watching CloudWatch for ThrottlingException; the client retries 5xx/429 with backoff.
const SUMMARY_CONCURRENCY = 8;

const RESOURCE_GROUPS = {
  instanceConfig: {
    label: 'Instance Configuration',
    items: {
      instanceConfig: 'Instance Configuration & Settings',
    },
  },
  amazonConnect: {
    label: 'Amazon Connect',
    items: {
      agentStatuses: 'Agent Statuses',
      hoursOfOperation: 'Hours of Operation',
      contactFlows: 'Contact Flows',
      contactFlowModules: 'Contact Flow Modules',
      phoneNumbers: 'Phone Numbers',      
      prompts: 'Prompts',
      queues: 'Queues',
      quickConnects: 'Quick Connects',
      routingProfiles: 'Routing Profiles',
      securityProfiles: 'Security Profiles',
      users: 'Users',
      userHierarchyGroups: 'User Hierarchy Groups'
    },
  },
  lambda: {
    label: 'Lambda',
    items: {
      functions: 'Functions',
    },
  },
  lex: {
    label: 'Lex',
    items: {
      lexBotV2: 'Lex Bot V2',
    },
  },
  trustedAdvisor: {
    label: 'Trusted Advisor',
    items: {
      recommendations: 'Recommendations',
    },
  },
};

const AWSExportPage = ({ onBackToPlatformSelector }) => {
  const { getApiToken } = useAuth();
  const { awsCredentials, setAwsCredentials } = useAwsCredentials();
  const [selectedResources, setSelectedResources] = useState({});
  const [selectedInstances, setSelectedInstances] = useState({});
  const [connectInstances, setConnectInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [openAccordionItems, setOpenAccordionItems] = useState(['instanceConfig']);
  const [exportStatus, setExportStatus] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [excelExporter, setExcelExporter] = useState(null);
  const [lambdaScanMode, setLambdaScanMode] = useState('balanced');
  const [generateReactFlowDiagrams, setGenerateReactFlowDiagrams] = useState(true);
  const [throttleStatus, setThrottleStatus] = useState({
    isThrottled: false,
    message: ''
  });
  
  // Always use balanced routing - no need for user configuration here
  const routingConfig = RoutingPresets.BALANCED;
  
  const [reactFlowDiagramConfig, setReactFlowDiagramConfig] = useState({
    includeMetadata: true,
    validateFlows: true,
    tooltips: true,
    skipDefaultFlows: false,
    skipEmptyFlows: true,
    generateSummary: true,
    showValidationResults: true,
    layoutDirection: 'TB'
  });
  
  // Diagram generation state
  const [reactFlowGenerationStatus, setReactFlowGenerationStatus] = useState({
    isGenerating: false,
    progress: 0,
    currentFlow: '',
    phase: 'idle',
    results: null
  });

  // Contact Flow Viewer state
  const [flowViewerState, setFlowViewerState] = useState({
    opened: false,
    flows: [],
    summary: null,
    instanceAlias: '',
    exportedData: null,
    instanceId: null,
    selectedInstanceIds: [],
    connectInstances: []
  });

  const [lambdaDataOptions, setLambdaDataOptions] = useState({
    includeRuntimeDeprecation: true,
    includeLogRetention: true,
    includeLastInvocation: true,
    includeInvocationCounts: true
  });

  const [lexV2Options, setLexV2Options] = useState({
    enableConversationAnalytics: false
  });

  // AI Analysis state
  const [aiAnalysisEnabled, setAiAnalysisEnabled] = useState(false);
  const [selectedAiServices, setSelectedAiServices] = useState({
    lambda: false,
    lexV2: false,
    connect: false,
    trustedAdvisor: false,
    contactFlowSummaries: false
  });
  const [selectedBedrockModel, setSelectedBedrockModel] = useState(getRecommendedModel().id);
  const [availableModels] = useState(BEDROCK_MODELS);
  const [aiAnalysisStatus, setAiAnalysisStatus] = useState({
    isAnalyzing: false,
    progress: 0,
    currentService: '',
    error: null,
    completed: false
  });

  // Contact Flow Summary tracking state
  const [contactFlowSummaryStatus, setContactFlowSummaryStatus] = useState({
    wasRequested: false,      // Did user enable summaries?
    isGenerating: false,      // Currently generating?
    completed: false,         // All summaries attempted?
    progress: { current: 0, total: 0 }  // Track progress
  });

  // Runtime deprecation state
  const [runtimeDeprecationStatus, setRuntimeDeprecationStatus] = useState({
    loading: false,
    loaded: false,
    error: null,
    lastUpdated: null
  });

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    opened: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmLabel: 'Continue',
    cancelLabel: 'Cancel',
    color: 'orange'
  });

  // Preview modal state
  const [previewModal, setPreviewModal] = useState({
    opened: false,
    flowData: null,
    summary: null
  });

  // Initialize Excel exporter
  useEffect(() => {
    const initializeExporter = async () => {
      try {
        const exporter = new ExcelExporter();
        await exporter.initialize();
        setExcelExporter(exporter);
      } catch (error) {
        console.error('Failed to initialize Excel exporter:', error);
      }
    };

    initializeExporter();
  }, []);

  // Initialize resource state with instanceConfig selected by default
  useEffect(() => {
    const initialResources = {};
    Object.entries(RESOURCE_GROUPS).forEach(([group, { items }]) => {
      Object.keys(items).forEach((item) => {
        initialResources[item] = item === 'instanceConfig';
      });
    });
    setSelectedResources(initialResources);
  }, []);

  // Load runtime deprecation data
  const loadRuntimeDeprecationData = useCallback(async () => {
    setRuntimeDeprecationStatus(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await runtimeDeprecationService.getRuntimeDeprecationData();
      setRuntimeDeprecationStatus({
        loading: false,
        loaded: true,
        error: null,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Failed to load runtime deprecation data:', error);
      setRuntimeDeprecationStatus({
        loading: false,
        loaded: false,
        error: error.message,
        lastUpdated: null
      });
    }
  }, []);

  // Pre-load runtime deprecation data when Lambda functions are selected
  useEffect(() => {
    if (selectedResources.functions && !runtimeDeprecationStatus.loading && !runtimeDeprecationStatus.loaded) {
      loadRuntimeDeprecationData();
    }
  }, [selectedResources.functions, runtimeDeprecationStatus.loading, runtimeDeprecationStatus.loaded, loadRuntimeDeprecationData]);

  // Auto-enable Conversation Analytics when Lex V2 is selected
  useEffect(() => {
    if (selectedResources.lexBotV2) {
      setLexV2Options(prev => ({
        ...prev,
        enableConversationAnalytics: true
      }));
    }
  }, [selectedResources.lexBotV2]);

    useEffect(() => {
    const handleThrottleEvent = (event) => {
      setThrottleStatus({
        isThrottled: event.detail.isThrottled,
        message: event.detail.message
      });
      
      // Auto-clear the throttle status after 3 seconds
      if (event.detail.isThrottled) {
        setTimeout(() => {
          setThrottleStatus(prev => ({
            ...prev,
            isThrottled: false
          }));
        }, 3000);
      }
    };

    window.addEventListener('throttle-status', handleThrottleEvent);
    return () => window.removeEventListener('throttle-status', handleThrottleEvent);
  }, []);


  // Fetch Connect Instances
  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);
      try {
        const instances = await listConnectInstances(awsCredentials, awsCredentials.region);
        const instanceState = {};
        instances.forEach((inst) => {
          instanceState[inst.Id] = false;
        });
        setConnectInstances(instances);
        setSelectedInstances(instanceState);
      } catch (error) {
        console.error('Failed to list Connect instances:', error);
      } finally {
        setLoadingInstances(false);
      }
    };

    if (awsCredentials?.accessKeyId) fetchInstances();
  }, [awsCredentials]);

  // Utility function to show confirmation modal
  const showConfirmation = ({ title, message, onConfirm, onCancel, confirmLabel = 'Continue', cancelLabel = 'Cancel', color = 'blue' }) => {
    setConfirmationModal({
      opened: true,
      title,
      message,
      onConfirm,
      onCancel,
      confirmLabel,
      cancelLabel,
      color
    });
  };

  // Handle confirmation modal close
  const handleConfirmationClose = () => {
    setConfirmationModal(prev => ({ ...prev, opened: false }));
    if (confirmationModal.onCancel) {
      confirmationModal.onCancel();
    }
  };

  // Handle confirmation modal confirm
  const handleConfirmationConfirm = () => {
    setConfirmationModal(prev => ({ ...prev, opened: false }));
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
  };

  const handleInstanceToggle = (id) => (e) => {
    setSelectedInstances((prev) => ({
      ...prev,
      [id]: e.currentTarget.checked,
    }));
  };

  const handleResourceToggle = (resource) => (e) => {
    setSelectedResources((prev) => ({
      ...prev,
      [resource]: e.currentTarget.checked,
    }));

    // If Lambda functions are being selected, pre-load runtime data
    if (resource === 'functions' && e.currentTarget.checked && !runtimeDeprecationStatus.loaded) {
      loadRuntimeDeprecationData();
    }
  };

  const handleGroupToggle = (groupKey) => (e) => {
    const checked = e.currentTarget.checked;
    const groupItems = RESOURCE_GROUPS[groupKey].items;
    setSelectedResources((prev) => {
      const updated = { ...prev };
      Object.keys(groupItems).forEach((item) => {
        updated[item] = checked;
      });
      return updated;
    });

    // Auto-expand accordion when group is checked
    if (checked) {
      setOpenAccordionItems((prev) => {
        if (!prev.includes(groupKey)) {
          return [...prev, groupKey];
        }
        return prev;
      });
    }

    // If Lambda group is being selected, pre-load runtime data
    if (groupKey === 'lambda' && checked && !runtimeDeprecationStatus.loaded) {
      loadRuntimeDeprecationData();
    }
  };

  // Diagram configuration handlers
  const handleReactFlowConfigChange = (key, value) => {
    setReactFlowDiagramConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Preview flow diagrams functionality
  const handlePreviewFlows = async () => {
    const selectedInstanceIds = Object.entries(selectedInstances)
      .filter(([_, isChecked]) => isChecked)
      .map(([id]) => id);

    const hasFlowsOrModules = selectedResources.contactFlows || selectedResources.contactFlowModules;
    if (selectedInstanceIds.length === 0 || !hasFlowsOrModules) {
      notifications.show({
        title: 'Selection Required',
        message: 'Please select at least one instance and enable contact flows or modules.',
        color: 'orange',
      });
      return;
    }

    try {
      setPreviewModal(prev => ({ ...prev, opened: true, flowData: null, summary: null }));
      
      const previewKeys = [];
      if (selectedResources.contactFlows) previewKeys.push('contactFlows');
      if (selectedResources.contactFlowModules) previewKeys.push('contactFlowModules');
      
      const previewData = await fetchResources({
        selectedResourceKeys: previewKeys,
        selectedInstanceIds,
        awsCredentials,
        contactFlowOptions: { generateDiagrams: false },
        onProgress: () => {}
      });

      // Use React Flow service for summary with data lookup
      const summary = reactFlowContactFlowService.getDiagramSummary(previewData, {
        ...reactFlowDiagramConfig,
        exportedData: previewData,
        instanceIds: selectedInstanceIds
      });
      
      setPreviewModal(prev => ({
        ...prev,
        flowData: previewData,
        summary
      }));
      
    } catch (error) {
      console.error('Failed to preview flows:', error);
      notifications.show({
        title: 'Preview Failed',
        message: error.message || 'Failed to preview contact flows and modules',
        color: 'red',
      });
      setPreviewModal(prev => ({ ...prev, opened: false }));
    }
  };

  const handleLambdaDataOptionChange = (key, value) => {
    setLambdaDataOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // View processed diagrams
  const handleViewDiagrams = () => {
    if (reactFlowGenerationStatus.results) {
      const selectedInstanceIds = Object.entries(selectedInstances)
        .filter(([_, isChecked]) => isChecked)
        .map(([id]) => id);
        
      const firstSelectedInstanceId = selectedInstanceIds[0];
      const instanceAlias = connectInstances.find(inst => inst.Id === firstSelectedInstanceId)?.InstanceAlias || 'Unknown';

      setFlowViewerState({
        opened: true,
        flows: reactFlowGenerationStatus.results.flows || [],
        summary: reactFlowGenerationStatus.results.summary || null,
        instanceAlias,
        exportedData: reactFlowGenerationStatus.results.exportedData || null,
        instanceId: firstSelectedInstanceId || null,
        selectedInstanceIds, // Pass all selected instance IDs
        connectInstances // Pass the instances array for lookup
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'loading':
        return <IconLoader size={14} className="animate-spin" />;
      case 'success':
        return <IconCheck size={14} color="green" />;
      case 'error':
        return <IconX size={14} color="red" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (resourceKey) => {
    const status = exportStatus[resourceKey];
    if (!status) return null;

    const { state, exported, total, errors, instancesCompleted, totalInstances } = status;
    
    if (state === 'loading') {
      if (total === 0) {
        return (
          <Badge variant="light" color="blue" size="sm">
            {getStatusIcon(state)} Scanning... ({instancesCompleted}/{totalInstances} instances)
          </Badge>
        );
      }
      return (
        <Badge variant="light" color="blue" size="sm">
          {getStatusIcon(state)} Exporting... {exported}/{total}
        </Badge>
      );
    }
    
    if (state === 'success') {
      return (
        <Badge variant="light" color="green" size="sm">
          {getStatusIcon(state)} Success {exported} found
        </Badge>
      );
    }
    
    if (state === 'error') {
      const errorText = errors > 0 ? ` (${errors} instance errors)` : '';
      return (
        <Badge variant="light" color="red" size="sm">
          {getStatusIcon(state)} {exported}/{total}${errorText}
        </Badge>
      );
    }

    return null;
  };

  const getProgressValue = (resourceKey) => {
    const status = exportStatus[resourceKey];
    if (!status || status.total === 0) return 0;
    return (status.exported / status.total) * 100;
  };

  const getRuntimeDeprecationBadge = () => {
    if (!selectedResources.functions) return null;

    if (runtimeDeprecationStatus.loading) {
      return (
        <Badge variant="light" color="blue" size="sm">
          <IconLoader size={12} className="animate-spin" style={{ marginRight: 4 }} />
          Loading runtime data...
        </Badge>
      );
    }

    if (runtimeDeprecationStatus.loaded) {
      return (
        <Badge variant="light" color="green" size="sm">
          <IconCheck size={12} style={{ marginRight: 4 }} />
          Runtime data ready
        </Badge>
      );
    }

    if (runtimeDeprecationStatus.error) {
      return (
        <Badge variant="light" color="orange" size="sm">
          <IconX size={12} style={{ marginRight: 4 }} />
          Using cached data
        </Badge>
      );
    }

    return null;
  };

  // Export function with React Flow diagram generation
  const handleExport = async () => {
    const selectedResourceKeys = Object.entries(selectedResources)
        .filter(([_, isChecked]) => isChecked)
        .map(([key]) => key);

    const selectedInstanceIds = Object.entries(selectedInstances)
        .filter(([_, isChecked]) => isChecked)
        .map(([id]) => id);

    if (selectedResourceKeys.length === 0 || selectedInstanceIds.length === 0) {
        notifications.show({
          title: 'Selection Required',
          message: 'Please select at least one resource and one instance.',
          color: 'orange',
        });
        return;
    }

    if (!excelExporter) {
        notifications.show({
          title: 'Not Ready',
          message: 'Excel exporter is not ready. Please try again in a moment.',
          color: 'orange',
        });
        return;
    }

    // Check if Lambda functions are selected but runtime data failed to load
    if (selectedResourceKeys.includes('functions') && runtimeDeprecationStatus.error && !runtimeDeprecationStatus.loaded) {
        showConfirmation({
          title: 'Runtime Deprecation Data Warning',
          message: 'Runtime deprecation data could not be loaded from AWS documentation. Lambda function export will continue but deprecation columns may show "Unknown". Do you want to proceed?',
          confirmLabel: 'Continue Export',
          cancelLabel: 'Cancel',
          color: 'orange',
          onConfirm: () => proceedWithExport(selectedResourceKeys, selectedInstanceIds),
          onCancel: () => {
            console.log('Export cancelled by user');
          }
        });
        return;
    }

    // Proceed directly if no runtime data issues
    await proceedWithExport(selectedResourceKeys, selectedInstanceIds);
  };

  // Export logic with React Flow diagram generation
  const proceedWithExport = async (selectedResourceKeys, selectedInstanceIds) => {
    setIsExporting(true);
    
    // Initialize export status for selected resources + instanceConfig
    const initialStatus = {};
    const allResourceKeys = [...new Set(['instanceConfig', ...selectedResourceKeys])];
    
    allResourceKeys.forEach(key => {
        initialStatus[key] = {
        state: 'loading',
        exported: 0,
        total: 0,
        errors: 0,
        instancesCompleted: 0,
        totalInstances: selectedInstanceIds.length
        };
    });
    setExportStatus(initialStatus);

    // Configure Lambda scanning based on user selection
    let lambdaOptions = {};
      if (selectedResourceKeys.includes('functions')) {
          switch (lambdaScanMode) {
              case 'recent':
                  lambdaOptions = createInvocationScanConfig.recentOnly();
                  break;
              case 'comprehensive':
                  lambdaOptions = createInvocationScanConfig.comprehensive();
                  break;
              case 'balanced':
              default:
                  lambdaOptions = createInvocationScanConfig.balanced();
                  break;
          }
          
          // Add data options to Lambda configuration
          lambdaOptions.dataOptions = lambdaDataOptions;
      }

    let contactFlowOptions = {};
    if (selectedResourceKeys.includes('contactFlows') || selectedResourceKeys.includes('contactFlowModules')) {
      contactFlowOptions = {
        generateDiagrams: generateReactFlowDiagrams
      };
    }
    
    // Contact Flow Error metrics are now handled by unified error metrics function in resourceDispatcher
    if (selectedResourceKeys.includes('contactFlows') || selectedResourceKeys.includes('contactFlowModules')) {
    }

    // Capture if Contact Flow Summaries were requested for tracking
    setContactFlowSummaryStatus({
      wasRequested: selectedAiServices.contactFlowSummaries && generateReactFlowDiagrams,
      isGenerating: false,
      completed: false,
      progress: { current: 0, total: 0 }
    });

    try {
        const results = await fetchResources({
            selectedResourceKeys,
            selectedInstanceIds,
            awsCredentials,
            lambdaOptions,
            contactFlowOptions,
            lexV2Options,
            onProgress: (resourceKey, progress) => {
            setExportStatus(prev => ({
                ...prev,
                [resourceKey]: {
                ...prev[resourceKey],
                ...progress
                }
            }));
            }
        });


        // Diagram generation if contact flows or modules are selected
        if ((selectedResourceKeys.includes('contactFlows') || selectedResourceKeys.includes('contactFlowModules')) && generateReactFlowDiagrams) {
          try {
            
            setReactFlowGenerationStatus({
              isGenerating: true,
              progress: 0,
              currentFlow: '',
              phase: 'starting'
            });
            
            const instanceAlias = connectInstances.find(inst => inst.Id === selectedInstanceIds[0])?.InstanceAlias || 'Unknown';
            
            // Generate React Flow diagrams with progress tracking
            const diagramResult = await reactFlowContactFlowService.generateDiagrams(
              results, 
              instanceAlias, 
              {
                ...reactFlowDiagramConfig,
                onProgress: (progress) => {
                  setReactFlowGenerationStatus(prev => ({
                    ...prev,
                    progress: (progress.processed / progress.total) * 100,
                    currentFlow: progress.flowName,
                    phase: progress.phase
                  }));
                }
              }
            );
            
            const { flows, summary } = diagramResult;
            
            setReactFlowGenerationStatus({
              isGenerating: false,
              progress: 100,
              currentFlow: '',
              phase: 'complete',
              results: { 
                flows, 
                summary,
                exportedData: results
              }
            });
            
            if (flows.length > 0) {
              const flowCount = flows.filter(f => f.resourceType === 'flow').length;
              const moduleCount = flows.filter(f => f.resourceType === 'module').length;
              const message = flowCount > 0 && moduleCount > 0 
                ? `Processed ${flowCount} flows and ${moduleCount} modules for visualization`
                : flowCount > 0 
                ? `Processed ${flowCount} contact flows for visualization`
                : `Processed ${moduleCount} contact flow modules for visualization`;
                
              notifications.show({
                title: 'React Flow Diagrams Ready',
                message,
                color: 'green',
              });

              // Show validation results if enabled
              if (reactFlowDiagramConfig.showValidationResults && summary.validationResults?.length > 0) {
                const validItems = summary.validationResults.filter(r => r.isValid).length;
                notifications.show({
                  title: 'Flow/Module Validation Complete',
                  message: `${validItems}/${summary.validationResults.length} items passed validation`,
                  color: validItems === summary.validationResults.length ? 'green' : 'orange',
                });
              }
            } else {
              notifications.show({
                title: 'No Diagrams Generated',
                message: 'No contact flows or modules with content found for diagram generation',
                color: 'yellow',
              });
            }
          } catch (error) {
            console.error('Failed to generate React Flow diagrams:', error);
            setReactFlowGenerationStatus({
              isGenerating: false,
              progress: 0,
              currentFlow: '',
              phase: 'error',
              results: null
            });
            notifications.show({
              title: 'Diagram Generation Failed',
              message: error.message || 'Failed to generate React Flow diagrams',
              color: 'red',
            });
          }
        }
        
        // Export to Excel - one workbook per instance
        const timestamp = new Date().toISOString().split('T')[0];
        const exportErrors = [];
        
        for (const instanceId of selectedInstanceIds) {
            try {
            // Find the instance alias for a better filename
            const instance = connectInstances.find(inst => inst.Id === instanceId);
            const instanceAlias = instance?.InstanceAlias || instanceId;
            
            // Create filename with instance identifier
            const filename = `AWS_Connect_Export_${instanceAlias}_${timestamp}.xlsx`;
            
            // Create data object for this specific instance
            const instanceData = {
                [instanceId]: results[instanceId]
            };
            
            // For global resources (Lambda functions, Lex bots), include them ONLY from the first instance
            // to avoid duplication since they're identical across all instances
            const firstInstanceId = selectedInstanceIds[0];
            if (selectedResourceKeys.includes('functions') && results[firstInstanceId]?.functions) {
                instanceData.functions = results[firstInstanceId].functions;
            }
            if (selectedResourceKeys.includes('lexBotV2') && results[firstInstanceId]?.lexBotV2) {
                instanceData.lexBotV2 = results[firstInstanceId].lexBotV2;
            }
            if (selectedResourceKeys.includes('recommendations') && results[firstInstanceId]?.recommendations) {
                instanceData.trustedAdvisor = results[firstInstanceId].recommendations;
            }
            console.log('--Exported Results--');
            console.log('--'+filename+'--');
            console.log(instanceData);
            // Export this instance's data to its own Excel file
            await excelExporter.export(instanceData, filename);
            
            } catch (error) {
            console.error(`Failed to export Excel for instance ${instanceId}:`, error);
            exportErrors.push({
                instanceId,
                error: error.message
            });
            }
        }
        
        // Show success/error messages
        if (exportErrors.length === 0) {
            notifications.show({
                title: 'Export Successful',
                message: `Successfully exported ${selectedInstanceIds.length} Excel file(s). Files have been downloaded.`,
                color: 'green',
            });
        } else if (exportErrors.length < selectedInstanceIds.length) {
            // Partial success
            const successCount = selectedInstanceIds.length - exportErrors.length;
            notifications.show({
                title: 'Partial Export Success',
                message: `${successCount} of ${selectedInstanceIds.length} files exported successfully. ${exportErrors.length} failed.`,
                color: 'yellow',
            });
        } else {
            // Total failure
            notifications.show({
                title: 'Export Failed',
                message: `Failed to export all Excel files. Please check the console for details.`,
                color: 'red',
            });
        }

        // AI Analysis step (if enabled)
        if (aiAnalysisEnabled && (selectedAiServices.lambda || selectedAiServices.lexV2 || selectedAiServices.connect)) {
            try {
                
                // Determine which services to analyze
                const servicesToAnalyze = [];
                if (selectedAiServices.lambda && selectedResourceKeys.includes('functions')) {
                    servicesToAnalyze.push('lambda');
                }
                if (selectedAiServices.lexV2 && selectedResourceKeys.includes('lexBotV2')) {
                    servicesToAnalyze.push('lexV2');
                }
                if (selectedAiServices.connect && selectedResourceKeys.includes('contactFlows')) {
                    servicesToAnalyze.push('connect');
                }
                if (selectedAiServices.trustedAdvisor && selectedResourceKeys.includes('recommendations')) {
                    servicesToAnalyze.push('trustedAdvisor');
                }

                if (servicesToAnalyze.length > 0) {
                    setAiAnalysisStatus({
                        isAnalyzing: true,
                        progress: 0,
                        currentService: '',
                        error: null,
                        completed: false
                    });

                    // Set up progress tracking
                    aiAnalysisService.setProgressCallback((progress, message) => {
                        setAiAnalysisStatus(prev => ({
                            ...prev,
                            progress,
                            currentService: message || prev.currentService
                        }));
                    });

                    // Perform AI analysis using API Gateway (auth token for authorization)
                    let apiToken = null;
                    try { apiToken = await getApiToken(); } catch (e) { console.warn('Failed to get API token:', e); }
                    const analysisResults = await aiAnalysisService.performAnalysis(
                        results,
                        servicesToAnalyze,
                        selectedBedrockModel,
                        null,
                        apiToken
                    );

                    // Generate Contact Flow Summaries (if enabled)
                    if (selectedAiServices.contactFlowSummaries && generateReactFlowDiagrams) {
                        try {
                            // Count total flows/modules to process
                            let totalFlows = 0;
                            for (const instanceId of selectedInstanceIds) {
                                const instanceData = results[instanceId];
                                if (instanceData?.contactFlows) totalFlows += instanceData.contactFlows.length;
                                if (instanceData?.contactFlowModules) totalFlows += instanceData.contactFlowModules.length;
                            }

                            // Initialize summary generation tracking
                            setContactFlowSummaryStatus(prev => ({
                                ...prev,
                                isGenerating: true,
                                progress: { current: 0, total: totalFlows }
                            }));

                            if (totalFlows > 0) {
                                // Flatten all flows + modules across instances into one task list,
                                // then summarize with bounded concurrency (Bedrock throttle is the
                                // limiter; the client retries 5xx/429 with backoff).
                                const summaryTasks = [];
                                for (const instanceId of selectedInstanceIds) {
                                    const instanceData = results[instanceId];
                                    if (instanceData?.contactFlows && Array.isArray(instanceData.contactFlows)) {
                                        for (const flow of instanceData.contactFlows) {
                                            summaryTasks.push({
                                                ref: flow,
                                                name: flow.Name,
                                                content: flow.Content,
                                                flowType: flow.ContactFlowType || 'CONTACT_FLOW',
                                                instanceId
                                            });
                                        }
                                    }
                                    if (instanceData?.contactFlowModules && Array.isArray(instanceData.contactFlowModules)) {
                                        for (const module of instanceData.contactFlowModules) {
                                            summaryTasks.push({
                                                ref: module,
                                                name: module.Name,
                                                content: module.Content,
                                                flowType: 'CONTACT_FLOW_MODULE',
                                                instanceId
                                            });
                                        }
                                    }
                                }

                                let processedFlows = 0;
                                const currentTotal = totalFlows;
                                const advanceProgress = () => {
                                    processedFlows++;
                                    const progressPercent = Math.round((processedFlows / currentTotal) * 100);
                                    setAiAnalysisStatus(prev => ({
                                        ...prev,
                                        progress: Math.min(85, progressPercent * 0.85), // Scale to 0-85%
                                        currentService: `Generating contact flow summaries... (${processedFlows}/${currentTotal})`
                                    }));
                                    setContactFlowSummaryStatus(prev => ({
                                        ...prev,
                                        progress: { current: processedFlows, total: currentTotal }
                                    }));
                                };

                                const limit = pLimit(SUMMARY_CONCURRENCY);
                                await Promise.all(summaryTasks.map(task => limit(async () => {
                                    try {
                                        if (!task.content) {
                                            // No flow content (e.g. Describe backfill failed) - nothing to summarize
                                            task.ref.summary = null;
                                            return;
                                        }

                                        const flowJson = typeof task.content === 'string'
                                            ? JSON.parse(task.content)
                                            : task.content;

                                        const result = await summarizeContactFlow(
                                            task.name,
                                            flowJson,
                                            {
                                                exportedData: results,
                                                instanceId: task.instanceId,
                                                selectedModel: selectedBedrockModel,
                                                flowType: task.flowType,
                                                accessToken: apiToken
                                            }
                                        );

                                        // Store summary on the originating flow/module object
                                        task.ref.summary = result.summary;
                                    } catch (error) {
                                        console.error(`Failed to summarize ${task.name}:`, error);
                                        // Continue with other flows even if one fails
                                        task.ref.summary = null;
                                    } finally {
                                        advanceProgress();
                                    }
                                })));

                                // Mark summary generation as complete
                                setContactFlowSummaryStatus(prev => ({
                                    ...prev,
                                    completed: true
                                }));
                            }
                        } catch (error) {
                            console.error('Contact flow summary generation failed:', error);
                            // Don't fail the entire process - continue with Word doc generation
                        } finally {
                            // Ensure isGenerating is set to false even if errors occurred
                            setContactFlowSummaryStatus(prev => ({
                                ...prev,
                                isGenerating: false,
                                completed: true // Mark complete even on error (per user requirement)
                            }));
                        }
                    }

                    // Generate Word document
                    setAiAnalysisStatus(prev => ({
                        ...prev,
                        progress: 90,
                        currentService: 'Generating Word document...'
                    }));

                    // Create filename for Word document - get account ID from first instance config
                    let accountId = 'Unknown';
                    try {
                      const firstInstanceId = selectedInstanceIds[0];
                      const instanceConfig = results[firstInstanceId]?.instanceConfig;
                      if (instanceConfig?.instanceDetails?.Arn) {
                        accountId = instanceConfig.instanceDetails.Arn.split(':')[4] || 'Unknown';
                      }
                    } catch (error) {
                      console.warn('Unable to extract account ID for filename:', error);
                    }
                    const wordFilename = `${accountId}_Assessment_${timestamp}.docx`;
                    
                    await wordDocumentService.generateDocument(analysisResults, wordFilename);

                    setAiAnalysisStatus({
                        isAnalyzing: false,
                        progress: 100,
                        currentService: '',
                        error: null,
                        completed: true
                    });

                    notifications.show({
                        title: 'AI Analysis Complete',
                        message: `AI analysis document "${wordFilename}" has been generated and downloaded.`,
                        color: 'blue',
                    });

                } else {
                }
            } catch (error) {
                console.error('AI analysis failed:', error);
                setAiAnalysisStatus({
                    isAnalyzing: false,
                    progress: 0,
                    currentService: '',
                    error: error.message,
                    completed: false
                });

                notifications.show({
                    title: 'AI Analysis Failed',
                    message: `Failed to generate AI analysis: ${error.message}`,
                    color: 'red',
                });
            }
        }
        
    } catch (error) {
        console.error('Export failed:', error);
        notifications.show({
            title: 'Export failed',
            message: error.message || `Failed to export`,
            color: 'red',
        });
    } finally {
        setIsExporting(false);
        setReactFlowGenerationStatus(prev => ({ ...prev, isGenerating: false }));
        setAiAnalysisStatus(prev => ({ ...prev, isAnalyzing: false }));
    }
  };;;

  return (
    <Layout onBackToPlatformSelector={onBackToPlatformSelector}>
      <Stack gap="lg">
        <Title order={3}>Export AWS Resources in Region {awsCredentials?.region}</Title>

        {/* AI analysis env-var sanity alert. Previously silent-failed on the
            first AI summarize/analyze action; now surfaced up front so users
            know why the AI buttons return errors. */}
        {!process.env.REACT_APP_ANALYSIS_API_BASE_URL && (
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow">
            <Text size="sm">
              AI Analysis backend not configured. Set
              {' '}<Code>REACT_APP_ANALYSIS_API_BASE_URL</Code>{' '}
              (and{' '}<Code>REACT_APP_SUMMARIZATION_API_URL</Code>) in your environment
              to enable AI-powered flow summarization, service analysis, and Word document
              generation. Resource export still works without it.
            </Text>
          </Alert>
        )}

        {/* AWS Credential Change Section */}
        <AWSCredentialChangeSection
          awsCredentials={awsCredentials}
          setAwsCredentials={setAwsCredentials}
          onCredentialsUpdated={() => {
            // Reset dependent state when credentials change
            setConnectInstances([]);
            setSelectedInstances({});
            setExportStatus({});

            // Clear React Flow generation results
            setReactFlowGenerationStatus({
              isGenerating: false,
              progress: 0,
              currentFlow: '',
              phase: 'idle',
              results: null
            });
          }}
        />

        {/* Runtime Deprecation Status Alert */}
        {selectedResources.functions && runtimeDeprecationStatus.error && (
          <Alert 
            icon={<IconInfoCircle size={16} />} 
            title="Runtime Deprecation Data Warning" 
            color="orange"
          >
            Could not fetch latest runtime deprecation data from AWS documentation. 
            Cached data will be used if available, otherwise deprecation columns will show "Unknown".
            <Button 
              variant="subtle" 
              size="xs" 
              onClick={loadRuntimeDeprecationData}
              loading={runtimeDeprecationStatus.loading}
              style={{ marginLeft: 8 }}
            >
              Retry
            </Button>
          </Alert>
        )}

        {throttleStatus.isThrottled && (
          <Alert 
            icon={<IconAlertTriangle size={16} />} 
            title="API Rate Limiting Active" 
            color="yellow"
          >
            {throttleStatus.message || 'Requests are being throttled to avoid AWS API limits. Export will continue at a reduced speed.'}
          </Alert>
        )}

        {/* Diagram Generation Status */}
         {reactFlowGenerationStatus.isGenerating && (
          <Card withBorder>
            <Group justify="space-between" mb="sm">
              <Text fw={500}>
                <IconTopologyStar size={16} style={{ marginRight: 8 }} />
                Generating React Flow Diagrams
              </Text>
              <Text size="sm" color="dimmed">
                {Math.round(reactFlowGenerationStatus.progress)}%
              </Text>
            </Group>
            
            <Progress 
              value={reactFlowGenerationStatus.progress} 
              mb="sm"
              color="blue"
              size="sm"
            />
            
            <Text size="sm" color="dimmed">
              {reactFlowGenerationStatus.phase === 'starting' && 'Initializing React Flow processing...'}
              {reactFlowGenerationStatus.phase === 'processing' && `Processing: ${reactFlowGenerationStatus.currentFlow}`}
              {reactFlowGenerationStatus.phase === 'complete' && 'React Flow diagram processing complete!'}
            </Text>
          </Card>
        )}

        {/* Show View Diagrams button if results are available */}
        {reactFlowGenerationStatus.results && !reactFlowGenerationStatus.isGenerating && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            color={contactFlowSummaryStatus.isGenerating ? "yellow" : "blue"}
          >
            <Group justify="space-between">
              <Text size="sm">
                {contactFlowSummaryStatus.isGenerating
                  ? `Generating AI summaries... (${contactFlowSummaryStatus.progress.current}/${contactFlowSummaryStatus.progress.total} flows complete)`
                  : 'Contact flow diagrams are ready for viewing'}
              </Text>
              <Tooltip
                label={contactFlowSummaryStatus.isGenerating
                  ? "Generating Contact Flow Summaries... Please wait."
                  : "View contact flow diagrams"}
                position="left"
              >
                <Button
                  size="xs"
                  onClick={handleViewDiagrams}
                  disabled={contactFlowSummaryStatus.isGenerating}
                  loading={contactFlowSummaryStatus.isGenerating}
                >
                  {contactFlowSummaryStatus.isGenerating
                    ? 'Generating Summaries...'
                    : 'View Diagrams'}
                </Button>
              </Tooltip>
            </Group>
          </Alert>
        )}

        <Box style={{ display: 'flex', gap: '2rem' }}>
          {/* Connect Instances */}
          <ScrollArea h={500} w="40%">
            <Stack gap="xs">
              <Title order={5}>Amazon Connect Instances</Title>
              {loadingInstances ? (
                <Loader size="sm" />
              ) : connectInstances.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No Connect instances found
                </Text>
              ) : (
                connectInstances.map((instance) => (
                  <Checkbox
                    key={instance.Id}
                    label={instance.InstanceAlias || instance.Id}
                    checked={selectedInstances[instance.Id] || false}
                    onChange={handleInstanceToggle(instance.Id)}
                    disabled={isExporting}
                  />
                ))
              )}
            </Stack>
          </ScrollArea>

          {/* Resource selection */}
          <ScrollArea h={500} w="60%">
            <Accordion multiple variant="default" value={openAccordionItems} onChange={setOpenAccordionItems}>
              {Object.entries(RESOURCE_GROUPS).map(([groupKey, group]) => {
                const items = group.items;
                const values = Object.entries(items);
                const allChecked = values.every(([key]) => selectedResources[key] || false);
                const someChecked = values.some(([key]) => selectedResources[key] || false);

                return (
                  <Accordion.Item value={groupKey} key={groupKey}>
                    <Accordion.Control>
                      <Box onClick={(e) => e.stopPropagation()} style={{ flexGrow: 1 }}>
                        <Group justify="space-between" align="center">
                          <Checkbox
                            label={group.label}
                            checked={allChecked}
                            indeterminate={!allChecked && someChecked}
                            onChange={handleGroupToggle(groupKey)}
                            disabled={isExporting}
                          />
                          {groupKey === 'lambda' && getRuntimeDeprecationBadge()}
                        </Group>
                      </Box>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xs" pl="md">
                        {values.map(([key, label]) => (
                          <Box key={key}>
                            <Group justify="space-between" align="center">
                              <Checkbox
                                label={label}
                                checked={selectedResources[key] || false}
                                onChange={handleResourceToggle(key)}
                                disabled={isExporting}
                              />
                              {getStatusBadge(key)}
                            </Group>
                            {exportStatus[key] && exportStatus[key].state === 'loading' && (
                              <Progress 
                                value={getProgressValue(key)} 
                                size="xs" 
                                mt="xs"
                                color="blue"
                              />
                            )}
                          </Box>
                        ))}
                        
                        {/* Lambda Configuration Section */}
                        {groupKey === 'lambda' && selectedResources.functions && (
                          <Box mt="md" p="sm" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                            <Text size="sm" fw={500} mb="xs">Lambda Configuration:</Text>
                            
                            {/* Invocation Scan Mode */}
                            <Text size="xs" fw={500} mb="xs">Invocation Scan Mode:</Text>
                            <Radio.Group 
                              value={lambdaScanMode} 
                              onChange={setLambdaScanMode}
                              size="xs"
                            >
                              <Stack gap="xs">
                                <Radio 
                                  value="recent" 
                                  label="Recent Only (30 days) - Fast"
                                  disabled={isExporting}
                                />
                                <Radio 
                                  value="balanced" 
                                  label="Balanced (6 months) - Recommended"
                                  disabled={isExporting}
                                />
                                <Radio 
                                  value="comprehensive" 
                                  label="Comprehensive (15 months) - Thorough"
                                  disabled={isExporting}
                                />
                              </Stack>
                            </Radio.Group>

                            {/* Data Collection Options */}
                              <Box mt="sm" pt="sm" style={{ borderTop: '1px solid #e9ecef' }}>
                                <Text size="xs" fw={500} mb="xs">Data Collection Options:</Text>
                                <Stack gap="xs">
                                  <Switch
                                    label="Include Runtime Deprecation"
                                    description="Fetch latest runtime deprecation dates from AWS documentation"
                                    checked={lambdaDataOptions.includeRuntimeDeprecation}
                                    onChange={(e) => handleLambdaDataOptionChange('includeRuntimeDeprecation', e.currentTarget.checked)}
                                    disabled={isExporting}
                                    size="xs"
                                  />
                                  <Switch
                                    label="Include Log Retention Policy"
                                    description="Query CloudWatch log group retention settings"
                                    checked={lambdaDataOptions.includeLogRetention}
                                    onChange={(e) => handleLambdaDataOptionChange('includeLogRetention', e.currentTarget.checked)}
                                    disabled={isExporting}
                                    size="xs"
                                  />
                                  <Switch
                                    label="Include Last Invocation Date"
                                    description="Scan CloudWatch metrics for last function invocation"
                                    checked={lambdaDataOptions.includeLastInvocation}
                                    onChange={(e) => handleLambdaDataOptionChange('includeLastInvocation', e.currentTarget.checked)}
                                    disabled={isExporting}
                                    size="xs"
                                  />
                                  <Switch
                                    label="Include Invocation and Error Counts"
                                    description="Fetch 30-day invocation and error metrics"
                                    checked={lambdaDataOptions.includeInvocationCounts}
                                    onChange={(e) => handleLambdaDataOptionChange('includeInvocationCounts', e.currentTarget.checked)}
                                    disabled={isExporting}
                                    size="xs"
                                  />
                                </Stack>
                              </Box>
                            
                            {/* Runtime Deprecation Info */}
                            <Box mt="sm" pt="sm" style={{ borderTop: '1px solid #e9ecef' }}>
                              <Text size="xs" fw={500} mb="xs">Runtime Deprecation:</Text>
                              <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                                ✨ Automatically fetches latest runtime deprecation dates from AWS documentation
                                and adds "Block Function Create" and "Block Function Update" columns to the export.
                              </Text>
                              {runtimeDeprecationStatus.lastUpdated && (
                                <Text size="xs" c="dimmed" mt="xs">
                                  Last updated: {runtimeDeprecationStatus.lastUpdated.toLocaleTimeString()}
                                </Text>
                              )}
                            </Box>
                            
                            <Text size="xs" c="dimmed" mt="xs" style={{ lineHeight: 1.4 }}>
                              {lambdaScanMode === 'recent' && '🚀 Scans only last 30 days. Fast but may miss older invocations.'}
                              {lambdaScanMode === 'balanced' && '⚡ Smart tiered scanning: recent data first, then deeper if needed. Good balance of speed and completeness.'}
                              {lambdaScanMode === 'comprehensive' && '🔍 Scans up to 15 months of CloudWatch data. Thorough but slower.'}
                            </Text>
                          </Box>
                        )}

                        {/* Lex V2 Configuration Section */}
                        {groupKey === 'lex' && selectedResources.lexBotV2 && (
                          <Box mt="md" p="sm" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                            <Text size="sm" fw={500} mb="xs">Lex V2 Configuration:</Text>
                            
                            <Switch
                              label="Enable Conversation Analytics Data"
                              description="Fetch last 30 days of conversation performance and utterance recognition metrics from CloudWatch"
                              checked={lexV2Options.enableConversationAnalytics}
                              onChange={(e) => setLexV2Options(prev => ({ 
                                ...prev, 
                                enableConversationAnalytics: e.currentTarget.checked 
                              }))}
                              disabled={isExporting}
                              size="sm"
                            />
                            
                            <Text size="xs" c="dimmed" mt="xs" style={{ lineHeight: 1.4 }}>
                              📊 Adds conversation performance metrics (Total/Dropped/Failed conversations) and utterance recognition metrics (Detected/Missed utterances) to the export.
                            </Text>
                          </Box>
                        )}

                        {/* Users Configuration Section */}
                        {groupKey === 'amazonConnect' && selectedResources.users && (
                          <Box mt="md" p="sm" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                            <Text size="sm" fw={500} mb="xs">Users Configuration:</Text>

                            <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                              ✅ Users are fetched in bulk via the Connect Search API, so full configuration
                              (routing profiles, security profiles, phone settings, name, and hierarchy groups)
                              is always included — no per-user lookups, no slow path to toggle.
                              <br />
                              ⚠️ The Search API does not return user email addresses, so the Email column is left blank.
                            </Text>
                          </Box>
                        )}

                        {/* Contact Flow Configuration Section - Simplified */}
                        {groupKey === 'amazonConnect' && (selectedResources.contactFlows || selectedResources.contactFlowModules) && (
                          <Card withBorder mt="md" p="sm" style={{ backgroundColor: '#f8f9fa' }}>
                            <Tabs defaultValue="basic" variant="pills" size="xs">
                              <Tabs.List mb="sm">
                                <Tabs.Tab value="basic" leftSection={<IconSettings size={14} />}>
                                  React Flow Settings
                                </Tabs.Tab>
                                <Tabs.Tab value="advanced" leftSection={<IconChartBar size={14} />}>
                                  Advanced Options
                                </Tabs.Tab>
                                <Tabs.Tab value="preview" leftSection={<IconEye size={14} />}>
                                  Preview
                                </Tabs.Tab>
                              </Tabs.List>

                              <Tabs.Panel value="basic">
                                <Stack gap="xs">
                                  <Switch
                                    label="Generate Interactive Diagrams"
                                    description="Create professional React Flow diagrams for contact flows"
                                    checked={generateReactFlowDiagrams}
                                    onChange={(e) => setGenerateReactFlowDiagrams(e.currentTarget.checked)}
                                    disabled={isExporting}
                                    size="sm"
                                  />

                                  {generateReactFlowDiagrams && (
                                    <Stack gap="xs" ml="md" mt="xs">
                                      <Switch
                                        label="Skip Empty Flows"
                                        description="Exclude flows with no content or actions"
                                        checked={reactFlowDiagramConfig.skipEmptyFlows}
                                        onChange={(e) => handleReactFlowConfigChange('skipEmptyFlows', e.currentTarget.checked)}
                                        disabled={isExporting}
                                        size="xs"
                                      />

                                      <Switch
                                        label="Validate Flows"
                                        description="Perform validation checks and report issues"
                                        checked={reactFlowDiagramConfig.validateFlows}
                                        onChange={(e) => handleReactFlowConfigChange('validateFlows', e.currentTarget.checked)}
                                        disabled={isExporting}
                                        size="xs"
                                      />
                                    </Stack>
                                  )}
                                </Stack>
                              </Tabs.Panel>

                              <Tabs.Panel value="advanced">
                                {generateReactFlowDiagrams && (
                                  <Stack gap="xs">
                                    <Switch
                                      label="Tooltips"
                                      description="Include comprehensive block metadata in diagrams"
                                      checked={reactFlowDiagramConfig.tooltips}
                                      onChange={(e) => handleReactFlowConfigChange('tooltips', e.currentTarget.checked)}
                                      disabled={isExporting}
                                      size="xs"
                                    />

                                    <Switch
                                      label="Show Validation Results"
                                      description="Display validation results in the interface"
                                      checked={reactFlowDiagramConfig.showValidationResults}
                                      onChange={(e) => handleReactFlowConfigChange('showValidationResults', e.currentTarget.checked)}
                                      disabled={isExporting}
                                      size="xs"
                                    />

                                    <Switch
                                      label="Generate Summary"
                                      description="Create summary statistics for all flows"
                                      checked={reactFlowDiagramConfig.generateSummary}
                                      onChange={(e) => handleReactFlowConfigChange('generateSummary', e.currentTarget.checked)}
                                      disabled={isExporting}
                                      size="xs"
                                    />
                                  </Stack>
                                )}
                              </Tabs.Panel>

                              <Tabs.Panel value="preview">
                                <Stack gap="xs">
                                  <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                                    Preview contact flows and modules to see what diagrams will be generated before running the full export.
                                  </Text>

                                  <Button
                                    variant="light"
                                    size="xs"
                                    leftSection={<IconEye size={14} />}
                                    onClick={handlePreviewFlows}
                                    disabled={isExporting || !selectedResources.contactFlows}
                                  >
                                    Preview Flows
                                  </Button>
                                </Stack>
                              </Tabs.Panel>
                            </Tabs>

                            <Divider mt="sm" />
                            <Text size="xs" c="dimmed" mt="xs" style={{ lineHeight: 1.4 }}>
                              🚀 React Flow integration creates professional interactive diagrams with
                              automatic layout, rich metadata, validation results, and export capabilities.
                              Advanced edge routing is automatically enabled for optimal diagram clarity.
                            </Text>
                          </Card>
                        )}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </ScrollArea>          
        </Box>

        {/* AI Analysis Configuration Section */}
        {(selectedResources.functions || selectedResources.lexBotV2) && (
          <Card withBorder mt="xl" p="md" style={{ backgroundColor: '#f0f8ff', borderColor: '#4c6ef5' }}>
            <Group mb="md">
              <IconBrain size={20} color="#4c6ef5" />
              <Text size="md" fw={600} color="#4c6ef5">
                AI Analysis Configuration
              </Text>
            </Group>
            
            <Stack gap="md">
              {/* Enable AI Analysis Toggle */}
              <Switch
                label="Enable AI Analysis"
                description="Generate AI-powered analysis and recommendations using AWS Bedrock"
                checked={aiAnalysisEnabled}
                onChange={(e) => setAiAnalysisEnabled(e.currentTarget.checked)}
                disabled={isExporting || aiAnalysisStatus.isAnalyzing}
                size="md"
                thumbIcon={
                  aiAnalysisEnabled ? (
                    <IconSparkles size={12} color="#4c6ef5" />
                  ) : (
                    <IconRobot size={12} color="gray" />
                  )
                }
              />

              {aiAnalysisEnabled && (
                <Box mt="md" p="md" style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e0e7ff' }}>
                  <Stack gap="md">
                    {/* Service Selection */}
                    <Box>
                      <Text size="sm" fw={500} mb="xs">
                        Select Services for AI Analysis:
                      </Text>
                      <Group gap="md">
                        {selectedResources.functions && (
                          <Checkbox
                            label="Lambda Functions"
                            description="Analyze runtime deprecations, performance, and cost optimization"
                            checked={selectedAiServices.lambda}
                            onChange={(e) => setSelectedAiServices(prev => ({
                              ...prev,
                              lambda: e.currentTarget.checked
                            }))}
                            disabled={isExporting || aiAnalysisStatus.isAnalyzing}
                          />
                        )}
                        
                        {selectedResources.lexBotV2 && (
                          <Checkbox
                            label="Lex V2 Bots"
                            description="Analyze conversation performance and bot optimization"
                            checked={selectedAiServices.lexV2}
                            onChange={(e) => setSelectedAiServices(prev => ({
                              ...prev,
                              lexV2: e.currentTarget.checked
                            }))}
                            disabled={isExporting || aiAnalysisStatus.isAnalyzing}
                          />
                        )}
                        
                        {selectedResources.contactFlows && (
                          <Checkbox
                            label="Amazon Connect"
                            description="Analyze contact flow error rates and performance per instance"
                            checked={selectedAiServices.connect}
                            onChange={(e) => setSelectedAiServices(prev => ({
                              ...prev,
                              connect: e.currentTarget.checked
                            }))}
                            disabled={isExporting || aiAnalysisStatus.isAnalyzing}
                          />
                        )}
                        
                        {selectedResources.recommendations && (
                          <Checkbox
                            label="Trusted Advisor"
                            description="Generate account-wide security and cost optimization summary"
                            checked={selectedAiServices.trustedAdvisor}
                            onChange={(e) => setSelectedAiServices(prev => ({
                              ...prev,
                              trustedAdvisor: e.currentTarget.checked
                            }))}
                            disabled={isExporting || aiAnalysisStatus.isAnalyzing}
                          />
                        )}

                        {selectedResources.contactFlows && generateReactFlowDiagrams && (
                          <Checkbox
                            label="Contact Flow Summaries"
                            description="Provide Summary of Contact Flows"
                            checked={selectedAiServices.contactFlowSummaries}
                            onChange={(e) => setSelectedAiServices(prev => ({
                              ...prev,
                              contactFlowSummaries: e.currentTarget.checked
                            }))}
                            disabled={isExporting || aiAnalysisStatus.isAnalyzing}
                          />
                        )}
                      </Group>
                    </Box>

                    {/* Model Selection */}
                    <Box>
                      <Text size="sm" fw={500} mb="xs">
                        AI Model Selection:
                      </Text>
                      <Select
                        value={selectedBedrockModel}
                        onChange={setSelectedBedrockModel}
                        data={availableModels.map(model => ({
                          value: model.id,
                          label: model.name,
                          description: model.description
                        }))}
                        disabled={isExporting || aiAnalysisStatus.isAnalyzing}
                        searchable
                        maxDropdownHeight={200}
                        withCheckIcon={false}
                        placeholder="Select Bedrock model..."
                      />
                      <Text size="xs" color="dimmed" mt="xs">
                        ⚡ AWS now offers all foundation models. A predefined list of recommended models is shown. Amazon Nova Micro is recommended by default for cost efficiency.
                      </Text>
                    </Box>

                    {/* Analysis Preview */}
                    {(selectedAiServices.lambda || selectedAiServices.lexV2) && (
                      <Box>
                        <Text size="sm" fw={500} mb="xs">
                          Analysis Preview:
                        </Text>
                        <Group gap="xs">
                          {selectedAiServices.lambda && (
                            <Badge variant="light" color="orange" leftSection={<IconSparkles size={12} />}>
                              Lambda Analysis
                            </Badge>
                          )}
                          {selectedAiServices.lexV2 && (
                            <Badge variant="light" color="blue" leftSection={<IconSparkles size={12} />}>
                              Lex V2 Analysis
                            </Badge>
                          )}
                          <Badge variant="light" color="green" leftSection={<IconFileText size={12} />}>
                            Word Document
                          </Badge>
                        </Group>
                        
                        <Text size="xs" color="dimmed" mt="xs" style={{ lineHeight: 1.4 }}>
                          🚀 AI analysis will generate a professional Word document with:
                          executive summary, critical findings, optimization opportunities, and actionable recommendations.
                        </Text>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Card>
        )}

        {/* AI Analysis Status */}
        {aiAnalysisStatus.isAnalyzing && (
          <Card withBorder>
            <Group justify="space-between" mb="sm">
              <Text fw={500}>
                <IconBrain size={16} style={{ marginRight: 8 }} />
                AI Analysis in Progress
              </Text>
              <Text size="sm" color="dimmed">
                {Math.round(aiAnalysisStatus.progress)}%
              </Text>
            </Group>
            
            <Progress 
              value={aiAnalysisStatus.progress} 
              mb="sm"
              color="blue"
              size="sm"
            />
            
            <Text size="sm" color="dimmed">
              {aiAnalysisStatus.currentService && `Analyzing ${aiAnalysisStatus.currentService}...`}
            </Text>
          </Card>
        )}

        <Group>
          <Button 
            leftSection={isExporting ? <Loader size={16} /> : <IconDownload size={16} />} 
            onClick={handleExport}
            disabled={isExporting || !excelExporter}
            loading={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
          {!excelExporter && (
            <Text size="sm" c="dimmed">
              Loading Excel template...
            </Text>
          )}
          {selectedResources.functions && (
            <Text size="sm" c="dimmed">
              Lambda scan mode: <strong>{lambdaScanMode}</strong>
            </Text>
          )}
          {generateReactFlowDiagrams && (
            <Text size="sm" c="dimmed">
              <IconTopologyStar size={14} style={{ marginRight: 4 }} />
              React Flow diagrams: <strong>Enabled</strong>
            </Text>
          )}
          {aiAnalysisEnabled && (selectedAiServices.lambda || selectedAiServices.lexV2) && (
            <Text size="sm" c="dimmed">
              <IconBrain size={14} style={{ marginRight: 4 }} />
              AI Analysis: <strong>Enabled</strong>
              {(selectedAiServices.lambda && selectedAiServices.lexV2) && ' (Lambda + Lex V2)'}
              {(selectedAiServices.lambda && !selectedAiServices.lexV2) && ' (Lambda)'}
              {(selectedAiServices.lexV2 && !selectedAiServices.lambda) && ' (Lex V2)'}
            </Text>
          )}
          {reactFlowGenerationStatus.results && (
            <Tooltip
              label={contactFlowSummaryStatus.isGenerating
                ? "Generating Contact Flow Summaries... Please wait."
                : "View contact flow diagrams"}
              position="left"
            >
              <Button
                variant="outline"
                size="sm"
                leftSection={<IconEye size={14} />}
                onClick={handleViewDiagrams}
                disabled={contactFlowSummaryStatus.isGenerating}
                loading={contactFlowSummaryStatus.isGenerating}
              >
                {contactFlowSummaryStatus.isGenerating
                  ? 'Generating Summaries...'
                  : 'View Diagrams'}
              </Button>
            </Tooltip>
          )}
        </Group>

        {/* Confirmation Modal */}
        <Modal
          opened={confirmationModal.opened}
          onClose={handleConfirmationClose}
          title={confirmationModal.title}
          size="md"
          centered
        >
          <Stack gap="lg">
            <Group>
              <IconAlertTriangle size={24} color="orange" />
              <Text size="sm" style={{ flex: 1 }}>
                {confirmationModal.message}
              </Text>
            </Group>
            
            <Group justify="flex-end" gap="sm">
              <Button 
                variant="subtle" 
                onClick={handleConfirmationClose}
              >
                {confirmationModal.cancelLabel}
              </Button>
              <Button 
                color={confirmationModal.color}
                onClick={handleConfirmationConfirm}
              >
                {confirmationModal.confirmLabel}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Preview Modal */}
        <Modal
          opened={previewModal.opened}
          onClose={() => setPreviewModal(prev => ({ ...prev, opened: false }))}
          title="Contact Flow Preview"
          size="lg"
          centered
        >
          {previewModal.summary ? (
            <Stack gap="md">
              <Card withBorder>
                <Title order={6} mb="sm">Flow Summary</Title>
                <Group>
                  <Text size="sm">
                    <strong>Total Flows:</strong> {previewModal.summary.totalFlows}
                  </Text>
                  <Text size="sm">
                    <strong>Diagramable:</strong> {previewModal.summary.diagramableFlows}
                  </Text>
                  <Text size="sm">
                    <strong>Default:</strong> {previewModal.summary.defaultFlows}
                  </Text>
                  <Text size="sm">
                    <strong>Empty:</strong> {previewModal.summary.flowsWithoutContent}
                  </Text>
                </Group>
              </Card>
              
              <Card withBorder>
                <Title order={6} mb="sm">Complexity Analysis</Title>
                <Group>
                  <Text size="sm">
                    <strong>Simple (1-5 actions):</strong> {previewModal.summary.complexityAnalysis.simple}
                  </Text>
                  <Text size="sm">
                    <strong>Moderate (6-15 actions):</strong> {previewModal.summary.complexityAnalysis.moderate}
                  </Text>
                  <Text size="sm">
                    <strong>Complex (16+ actions):</strong> {previewModal.summary.complexityAnalysis.complex}
                  </Text>
                </Group>
                <Text size="sm" mt="xs">
                  <strong>Average Actions per Flow:</strong> {previewModal.summary.complexityAnalysis.averageActions}
                </Text>
              </Card>
              
              {Object.keys(previewModal.summary.flowsByType).length > 0 && (
                <Card withBorder>
                  <Title order={6} mb="sm">Flow Types</Title>
                  <Stack gap="xs">
                    {Object.entries(previewModal.summary.flowsByType).map(([type, count]) => (
                      <Group key={type} justify="space-between">
                        <Text size="sm">{type}</Text>
                        <Badge variant="light" size="sm">{count}</Badge>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              )}
            </Stack>
          ) : (
            <Group justify="center" p="xl">
              <Loader size="md" />
              <Text>Loading flow preview...</Text>
            </Group>
          )}
        </Modal>

        {/* Contact Flow Viewer Modal */}
        <ContactFlowViewer
          opened={flowViewerState.opened}
          onClose={() => setFlowViewerState(prev => ({ ...prev, opened: false }))}
          flows={flowViewerState.flows}
          summary={flowViewerState.summary}
          instanceAlias={flowViewerState.instanceAlias}
          exportedData={flowViewerState.exportedData}
          instanceId={flowViewerState.instanceId}
          selectedInstanceIds={flowViewerState.selectedInstanceIds}
          connectInstances={flowViewerState.connectInstances}
          routingConfig={routingConfig}
        />
      </Stack>
    </Layout>
  );
};

export default AWSExportPage;